import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Instagram, Facebook } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { usePublicSettings } from "@/context/SettingsContext";
import SectionHeading from "@/components/SectionHeading";

export default function Contact() {
  const { t } = useLang();
  const s = usePublicSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const wa = (s.whatsapp_number || "").replace(/[^0-9]/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading overline={t("contact.overline")} title={t("contact.title")} subtitle={t("contact.subtitle")} />

        <div className="mt-16">
          {/* Info */}
          <div className="space-y-8 max-w-2xl">
            {s.contact_email && (
              <a href={`mailto:${s.contact_email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <span className="w-12 h-12 rounded-sm border border-line flex items-center justify-center text-gold group-hover:bg-petrol group-hover:text-white transition-all"><Mail size={18} strokeWidth={1.5} /></span>
                <div><span className="overline text-[10px] text-muted-foreground block">{t("contact.email_label")}</span><span className="text-petrol">{s.contact_email}</span></div>
              </a>
            )}
            {s.contact_phone && (
              <a href={`tel:${s.contact_phone}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                <span className="w-12 h-12 rounded-sm border border-line flex items-center justify-center text-gold group-hover:bg-petrol group-hover:text-white transition-all"><Phone size={18} strokeWidth={1.5} /></span>
                <div><span className="overline text-[10px] text-muted-foreground block">{t("contact.phone_label")}</span><span className="text-petrol">{s.contact_phone}</span></div>
              </a>
            )}
            {wa && (
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
                <span className="w-12 h-12 rounded-sm border border-line flex items-center justify-center text-gold group-hover:bg-petrol group-hover:text-white transition-all"><MessageCircle size={18} strokeWidth={1.5} /></span>
                <div><span className="overline text-[10px] text-muted-foreground block">{t("contact.whatsapp")}</span><span className="text-petrol">{s.whatsapp_number}</span></div>
              </a>
            )}
            {s.address && (
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-sm border border-line flex items-center justify-center text-gold"><MapPin size={18} strokeWidth={1.5} /></span>
                <div><span className="overline text-[10px] text-muted-foreground block">{t("contact.address_label")}</span><span className="text-petrol">{s.address}</span></div>
              </div>
            )}
            <div className="flex gap-4 pt-2">
              {s.social_instagram && <a href={s.social_instagram} target="_blank" rel="noreferrer" className="text-petrol/70 hover:text-gold transition-colors"><Instagram size={22} strokeWidth={1.5} /></a>}
              {s.social_facebook && <a href={s.social_facebook} target="_blank" rel="noreferrer" className="text-petrol/70 hover:text-gold transition-colors"><Facebook size={22} strokeWidth={1.5} /></a>}
            </div>
            {s.map_embed && (
              <div className="aspect-[16/10] rounded-sm overflow-hidden border border-line mt-4">
                <iframe title="map" src={s.map_embed} width="100%" height="100%" style={{ border: 0 }} loading="lazy" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
